import { create, useStore } from 'zustand'
import { persist } from 'zustand/middleware'

type Stage =
  | 'sophisticated'
  | 'experienced'
  | 'inexperienced'
  | 'initial'
  | 'assessExperience'
  | 'lowTestScore'

type FinishedScreen = 'sophisticated' | 'unsophisticated'

type InvestorAnalysisStorage = {
  stage: Stage
  setStage: (stage: Stage) => void

  finishedScreen: FinishedScreen | null
  setFinishedScreen(finishedScreen: FinishedScreen | null): void

  // odpoveď na úvodnú otázku (vetvenie), posiela sa s dotazníkom do API
  isSophisticatedInvestor: boolean | null
  setIsSophisticatedInvestor(isSophisticatedInvestor: boolean | null): void

  reset: () => void
  remove: () => void
}

// Mapa store-ov podľa userId - zaisťuje, že každý používateľ má svoj vlastný zdieľaný store
const stores = new Map<string, ReturnType<typeof _createStore>>()

export const useInvestorAnalysisStore = (userId: string | null) => {
  if (!userId) {
    throw new Error('User ID is missing')
  }

  // Ak už existuje store pre tohto používateľa, vráť ho, inak vytvor nový
  if (!stores.has(userId)) {
    stores.set(userId, _createStore(userId))
  }

  const store = stores.get(userId)!
  // Použijeme useStore hook na reaktívne subskribovanie na zmeny v store
  // Vrátime state aj metódy z store objektu
  const state = useStore(store)
  return {
    ...state,
    setStage: store.getState().setStage,
    setFinishedScreen: store.getState().setFinishedScreen,
    setIsSophisticatedInvestor: store.getState().setIsSophisticatedInvestor,
    reset: store.getState().reset,
    remove: store.getState().remove,
  }
}

function _createStore(userId: string) {
  // `-app3`: nový kľúč zahodil medzistavy rozpracované pred ukladaním dotazníka do API
  const storageName = `investor-analysis-store-${userId}-app3`
  const storage = {
    getItem: (name: string) => {
      const str = localStorage.getItem(name)
      return str ? JSON.parse(str) : null
    },
    setItem: (name: string, value: any) => {
      localStorage.setItem(name, JSON.stringify(value))
    },
    removeItem: (name: string) => {
      localStorage.removeItem(name)
    },
  }

  const store = create<InvestorAnalysisStorage>()(
    persist(
      (set) => ({
        stage: 'initial',
        setStage: (stage) => set({ stage }),

        finishedScreen: null,
        setFinishedScreen: (finishedScreen) => set({ finishedScreen }),

        isSophisticatedInvestor: null,
        setIsSophisticatedInvestor: (isSophisticatedInvestor) =>
          set({ isSophisticatedInvestor }),

        reset: () =>
          set({
            finishedScreen: null,
            stage: 'initial',
            isSophisticatedInvestor: null,
          }),
        remove: () => {
          // Použitie nativného Zustand persist storage API - najprv vyčistíme storage
          storage.removeItem(storageName)
          // Potom odstránime store z Mapy
          stores.delete(userId)
        },
      }),
      { name: storageName, storage }
    )
  )

  return store
}
